/**
 * Contexto de autenticación para manejar el estado del usuario.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = async () => {
    const token = authService.getToken();
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await authService.getMe();
      setUser(me);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(me));
    } catch {
      authService.logout();
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      await authService.login(email, password);
      const me = await authService.getMe();
      setUser(me);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(me));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.detail || error.message || 'Error signing in',
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      await authService.register(username, email, password);
      return await login(email, password);
    } catch (error) {
      return {
        success: false,
        error: error.detail || error.message || 'Error registering user',
      };
    }
  };

  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const isOwner = user?.role === 'owner';

  const value = {
    isAuthenticated,
    user,
    role: user?.role || null,
    isOwner,
    loading,
    login,
    register,
    logout,
    refreshUser: loadUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
